import { Injectable } from "@angular/core";
import moment from "moment";
import { HttpClient } from "@angular/common/http";
import { LoginService } from "./login.service";
import { Subject } from "rxjs";
import { ReconnectingSocket, socketOrigin } from "./reconnecting-socket";

@Injectable({
  providedIn: "root",
})
export class AppointmentsService {
  AutoIndex = null;
  startDate: Date;
  endDate: Date;
  duration: number;
  examType;
  examLength;
  lmp;
  examCompletedSocket: ReconnectingSocket;
  // the clinic the socket is bound to: a different clinic means a new socket
  private examCompletedSocketGid: unknown;
  examStatusIncomming = new Subject<string>();

  // Holds the appointment data we last opened:
  appointmentToNavBack;

  constructor(private http: HttpClient, public ls: LoginService) {}

  addEvent(appointment) {
    return this.http.post("/api/appointment", appointment);
  }

  async fetchEvents(
    startDate,
    endDate,
    isAll: Event,
    roomIds?: string[],
    doctorIds?: string[]
  ): Promise<any> {
    try {
      const start = startDate.split("T")[0];
      const end = endDate.split("T")[0];
      const params: any = { start, end };

      if (roomIds?.length > 0) {
        params["RID"] = roomIds;
      } else if (doctorIds?.length > 0) {
        params["UID"] = doctorIds;
      } else {
        if (this.ls.premissions?.ApptsByRoom) {
          params["RID"] = Array.isArray(this.ls.room) ? this.ls.room : [this.ls.room];
        } else {
          // If ls.doctor is null but there's only one doctor, use that doctor's UID
          if (!this.ls.doctor && this.ls.clinicUsers?.length === 1 && this.ls.userType == 'secretary') {
            this.ls.doctor = this.ls.clinicUsers[0].UID;
            params["UID"] = this.ls.clinicUsers[0].UID;
          } else {
            params["UID"] = this.ls.doctor;
          }
        }
      }

      return await this.http.get("api/appointment", { params }).toPromise();
    } catch (error) {
      console.error("Error fetching events:", error);
      throw error;
    }
  }

  calculateDuration(startDate: Date, endDate: Date): number {
    const startMoment = moment(startDate);
    const endMoment = moment(endDate);
    const duration = endMoment.diff(startMoment);
    const durationInMinutes = moment.duration(duration).asMinutes();

    return durationInMinutes;
  }

  setAppointment({ AutoIndex, aptlength, aptTime, examType, PID }) {
    this.AutoIndex = AutoIndex;
    this.duration = aptlength;
    this.startDate = new Date(aptTime);
    this.examType = examType;
    this.endDate = null;
  }

  async delete(): Promise<boolean> {
    if (this.AutoIndex == null) {
      throw new Error("AutoIndex is not set.");
    }

    try {
      await this.http.delete(`/api/appointment/${this.AutoIndex}`).toPromise();
      this.resetService();
      return true;
    } catch (error) {
      console.error("Error deleting event:", error);
      throw error;
    }
  }

  async updateAppointment(appointment): Promise<any> {
    if (appointment.AutoIndex == null) {
      throw new Error("AutoIndex is not set.");
    }

    try {
      return this.http.put(`/api/appointment`, appointment).toPromise();
    } catch (error) {
      console.error("Error update event:", error);
      throw error;
    }
  }

  async transferAppointments(
    appointmentIds: number[],
    targetDate: string,
    target: { targetUID?: number; targetRID?: number } = {}
  ): Promise<any> {
    try {
      return await this.http
        .post(`/api/appointment/transfer`, { appointmentIds, targetDate, ...target })
        .toPromise();
    } catch (error) {
      console.error("Error transferring appointments:", error);
      throw error;
    }
  }

  async saveInWaitingList(appointment): Promise<any> {
    try {
      return this.http.post(`/api/appointment/waitingList`, appointment).toPromise();
    } catch (error) {
      console.error("Error save im waiting list :", error);
      throw error;
    }
  }

    resetService() {
    this.AutoIndex = null;
    this.startDate = null;
    this.endDate = null;
    this.duration = null;
    this.examType = null;
    this.lmp = null;
  }

  appointmentHour(Date) {
    const startDate = Date;
    if (startDate) {
      const hours = startDate.getHours().toString().padStart(2, "0");
      const minutes = startDate.getMinutes();
      const startHour = `${hours}:${minutes.toString().padStart(2, "0")}`;
      return startHour;
    }
  }

  async arrived(appointmentId, PID, value) {
    return this.http.put(`/api/appointment/arrived`, { appointmentId, PID, value }).toPromise();
  }

  calcAppointmentPrice(appointment) {
    return this.http.post("/api/appointment/Price", appointment);
  }

  findClosestAppointment(appointmentDate, slotDuration) {
    const body = { appointmentDate, slotDuration };

    if (this.ls.premissions?.ApptsByRoom) {
      body["RID"] = this.ls.room;
    } else {
      // If ls.doctor is null but there's only one doctor, use that doctor's UID
      if (!this.ls.doctor && this.ls.clinicUsers?.length === 1) {
        body["UID"] = this.ls.clinicUsers[0].UID;
      } else {
        body["UID"] = this.ls.doctor;
      }
    }
    return this.http.post("/api/appointment/closest", body);
  }

  async getFutureAppointmentsByPid(pid: number): Promise<any[]> {
    try {
      const result: any = await this.http.get(`/api/appointment/patient`, { params: { PID: pid.toString() } }).toPromise();
      return result?.current ?? [];
    } catch (error) {
      console.error("Error fetching future appointments by PID:", error);
      return [];
    }
  }

  async getAppointmentsMadeByPatient(statuses): Promise<any> {
    try {
      const params = { status: statuses };
      return await this.http.get("api/appointment/madeByPatients", { params }).toPromise();
    } catch (error) {
      console.error("Error fetching patient appointments:", error);
      return [];
    }
  }

  async getNotApprovedAppointments(): Promise<any> {
    try {
      return await this.http.get("api/appointment/notApproved").toPromise();
    } catch (error) {
      console.error("Error fetching not approved appointments:", error);
      return [];
    }
  }

  async changeStatus(newStatus, appointmentId): Promise<any> {
    try {
      return await this.http
        .put("api/appointment/changeStatus", { status: newStatus, appointmentId })
        .toPromise();
    } catch (error) {
      console.error("Error update appointment status:", error);
      return [];
    }
  }

  async getPatientsMessages(): Promise<any> {
    try {
      return await this.http.get("api/appointment/messages").toPromise();
    } catch (error) {
      console.error("Error fetching patient messages:", error);
      return [];
    }
  }

  async deleteMsg(id): Promise<any> {
    try {
      return await this.http.delete(`api/appointment/messages/${id}`).toPromise();
    } catch (error) {
      console.error("Error delete patient messages:", error);
      return [];
    }
  }
  async getLeads(filters: { [key: string]: string }): Promise<any> {
    try {
      return await this.http.get("api/leads", { params: filters }).toPromise();
    } catch (error) {
      console.error("Error fetching leads:", error);
      return [];
    }
  }

  createLead(lead) {
    return this.http.post("api/leads", lead);
  }

  setLeadHandled(id, handled) {
    return this.http.put(`api/leads/${id}/handled`, { handled });
  }

  updateLeadNote(id, Note) {
    return this.http.put(`api/leads/${id}/note`, { Note });
  }

  createPatientFromLead(id, Birth, ID = null) {
    return this.http.post(`api/leads/${id}/patient`, { Birth, ID });
  }

  deleteLead(id) {
    return this.http.delete(`api/leads/${id}`);
  }

  searchAvailableSlots(data) {
    return this.http.post("api/appointment/slots", data);
  }

  // A ReconnectingSocket: the plain WebSocket was never re-established once it dropped, so after
  // every deploy (or a laptop waking up) the calendar silently stopped updating until F5.
  openExamCompletedSocket(gid = null) {
    if (this.examCompletedSocket && this.examCompletedSocketGid === gid) return; // already on this clinic
    this.closeExamCompletedSocket();
    this.examCompletedSocketGid = gid;
    this.examCompletedSocket = new ReconnectingSocket(
      () => socketOrigin() + "/api/exam-completed" + (gid ? "/" + gid : ""),
      (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "examCompleted") {
            this.examStatusIncomming.next(null);
          }
        } catch {
          this.examStatusIncomming.next(null);
        }
      },
      "exam-completed",
      // An exam may have completed while the socket was down: re-read once after a reconnect.
      (reconnected) => { if (reconnected) this.examStatusIncomming.next(null); }
    );
  }

  closeExamCompletedSocket() {
    if (this.examCompletedSocket) this.examCompletedSocket.close();
    this.examCompletedSocket = null;
    this.examCompletedSocketGid = undefined;
  }

  sendAppointmentSms(
    autoIndex: number,
    body: {
      PID: number;
      immediate?: boolean;
      update?: boolean;
      mobileOverride?: string;
      isUpdate?: boolean;
    }
  ) {
    return this.http.post(`api/appointment/${autoIndex}/sms`, body);
  }

  async searchAppointmentsByCreator(
    status: "all" | "secretary" | "patient",
    from?: Date | string,
    to?: Date | string,
    limit: number = 200,
    offset: number = 0,
    examTypeID?: number | "ALL",
    source?: "whatsapp" | "clinic_site" | "direct"
  ): Promise<{ rows: any[]; totalAppts: number }> {
    try {
      const _from = from ?? this.startDate ?? new Date();
      const _to = to ?? this.endDate ?? _from;

      const fromStr =
        typeof _from === "string"
          ? _from.split("T")[0] + " 00:00:00"
          : moment(_from).startOf("day").format("YYYY-MM-DD HH:mm:ss");

      const toStr =
        typeof _to === "string"
          ? _to.split("T")[0] + " 23:59:59"
          : moment(_to).endOf("day").format("YYYY-MM-DD HH:mm:ss");

      const params: any = { status, from: fromStr, to: toStr, limit, offset };

      if (examTypeID && examTypeID !== "ALL") {
        params.examTypeID = examTypeID;
      }

      if (source) {
        params.source = source;
      }

      return await this.http
        .get<{ rows: any[]; totalAppts: number }>("/api/appointment/stats", { params })
        .toPromise();
    } catch (error) {
      console.error("Error searching appointments:", error);
      return { rows: [], totalAppts: 0 };
    }
  }

  async searchCompletedAppointments(
    status: "all" | "secretary" | "patient",
    from?: Date | string,
    to?: Date | string,
    limit: number = 200,
    offset: number = 0,
    examTypeID?: number | "ALL",
    source?: "whatsapp" | "clinic_site" | "direct"
  ): Promise<{ rows: any[]; totalAppts: number }> {
    try {
      const _from = from ?? this.startDate ?? new Date();
      const _to = to ?? this.endDate ?? _from;

      // force start/end of day (keep parity with existing method)
      const fromStr =
        typeof _from === "string"
          ? _from.split("T")[0] + " 00:00:00"
          : moment(_from).startOf("day").format("YYYY-MM-DD HH:mm:ss");

      const toStr =
        typeof _to === "string"
          ? _to.split("T")[0] + " 23:59:59"
          : moment(_to).endOf("day").format("YYYY-MM-DD HH:mm:ss");

      const params: any = { status, from: fromStr, to: toStr, limit, offset };

      if (examTypeID && examTypeID !== "ALL") {
        params.examTypeID = examTypeID;
      }

      if (source) {
        params.source = source;
      }

      return await this.http
        .get<{ rows: any[]; totalAppts: number }>("/api/appointment/stats/completed", { params })
        .toPromise();
    } catch (error) {
      console.error("Error searching completed appointments:", error);
      return { rows: [], totalAppts: 0 };
    }
  }

  async searchNotCompletedAppointments(
    status: "all" | "secretary" | "patient",
    from?: Date | string,
    to?: Date | string,
    limit: number = 200,
    offset: number = 0,
    examTypeID?: number | "ALL",
    source?: "whatsapp" | "clinic_site" | "direct"
  ): Promise<{ rows: any[]; totalAppts: number }> {
    try {
      const _from = from ?? this.startDate ?? new Date();
      const _to = to ?? this.endDate ?? _from;

      const fromStr =
        typeof _from === "string"
          ? _from.split("T")[0] + " 00:00:00"
          : moment(_from).startOf("day").format("YYYY-MM-DD HH:mm:ss");

      const toStr =
        typeof _to === "string"
          ? _to.split("T")[0] + " 23:59:59"
          : moment(_to).endOf("day").format("YYYY-MM-DD HH:mm:ss");

      const params: any = { status, from: fromStr, to: toStr, limit, offset };

      if (examTypeID && examTypeID !== "ALL") {
        params.examTypeID = examTypeID;
      }

      if (source) {
        params.source = source;
      }

      return await this.http
        .get<{ rows: any[]; totalAppts: number }>("/api/appointment/stats/not-completed", { params })
        .toPromise();
    } catch (error) {
      console.error("Error searching not-completed appointments:", error);
      return { rows: [], totalAppts: 0 };
    }
  }

  async getRoomPlanning(
    RID: number,
    from: string,
    to: string
  ): Promise<{
    totalSlots: number;
    scheduledAppointments: number;
    freeSlots: number;
    utilizationPct: number;
    dailyBreakdown: Array<{ date: string; availableSlots: number; scheduledAppointments: number; freeSlots: number }>;
  }> {
    try {
      const params: any = { RID, from, to };
      return await this.http
        .get<any>("/api/appointment/stats/room-planning", { params })
        .toPromise();
    } catch (error) {
      console.error("Error fetching room planning:", error);
      return { totalSlots: 0, scheduledAppointments: 0, freeSlots: 0, utilizationPct: 0, dailyBreakdown: [] };
    }
  }
}
